import { useCallback, useEffect, useRef, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import {
  authStorage,
  clearAuthSession,
  getLoginRedirectPath,
  invalidateServerSession,
  AUTH_USER_UPDATED_EVENT,
} from '@/lib/auth'
import { getCurrentTenant } from '@/lib/tenant'
import {
  getLastActivity,
  resetActivity,
  recordActivity,
  isBusy,
  hasUnsavedChanges,
} from '@/lib/session-activity'
import {
  fetchSessionConfig,
  resolveTimeoutMinutes,
  isWithinOperatingHours,
  type ResolvedSessionConfig,
} from '@/lib/session-config'
import { isStaffPortalRole, ALL_TIMEOUT_ROLES } from '@/lib/roles'

// RL-020: automatic timeout applies to ALL authenticated roles (requirement: "All Users").
// Staff-portal roles additionally respect the shift-aware gate; customers and platform
// admins use the timeout window only (no shift hours apply to them).
// Role constants are sourced from @/lib/roles — isStaffPortalRole governs the shift-aware gate.

const ACTIVITY_EVENTS: Array<keyof WindowEventMap> = [
  'mousemove',
  'mousedown',
  'keydown',
  'touchstart',
  'scroll',
  'wheel',
]

function formatCountdown(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000))
  const m = Math.floor(total / 60)
  const s = total % 60
  return `${m}:${s.toString().padStart(2, '0')}`
}

/**
 * RL-020 — Automatic session timeout manager (all authenticated roles).
 * Mounted once, globally. Self-gates: inert for logged-out visitors.
 * Detects inactivity, shows a countdown warning, and performs a real logout.
 * Staff-portal roles additionally respect the shift-aware gate and busy/dirty signals.
 * Customers and platform admins use the inactivity window only (no shift gate).
 */
export function SessionTimeoutManager() {
  const [user, setUser] = useState(() => authStorage.getUser())
  const [config, setConfig] = useState<ResolvedSessionConfig | null>(null)
  const [showWarning, setShowWarning] = useState(false)
  const [remainingMs, setRemainingMs] = useState(0)
  const [unsavedAtWarning, setUnsavedAtWarning] = useState(false)

  const warningDeadlineRef = useRef<number | null>(null)
  const terminatingRef = useRef(false)

  const role = user?.role?.toUpperCase()
  const isStaffRole = isStaffPortalRole(role)
  const active =
    !!user && !!role && ALL_TIMEOUT_ROLES.has(role) && !!authStorage.getAccessToken()

  const timeoutMinutes = config ? resolveTimeoutMinutes(config, user?.posPermissions) : 0
  const enabled = active && !!config && config.enabled && timeoutMinutes > 0

  // Keep `user` in sync with login/logout across the app (and other tabs).
  useEffect(() => {
    const onAuthUpdated = () => setUser(authStorage.getUser())
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'user' || e.key === 'accessToken' || e.key === null) {
        setUser(authStorage.getUser())
      }
    }
    window.addEventListener(AUTH_USER_UPDATED_EVENT, onAuthUpdated)
    window.addEventListener('storage', onStorage)
    return () => {
      window.removeEventListener(AUTH_USER_UPDATED_EVENT, onAuthUpdated)
      window.removeEventListener('storage', onStorage)
    }
  }, [])

  // Load (or clear) session config as the active authenticated user changes.
  useEffect(() => {
    let cancelled = false
    if (!active) {
      setConfig(null)
      return
    }
    fetchSessionConfig().then((cfg) => {
      if (!cancelled) setConfig(cfg)
    })
    return () => {
      cancelled = true
    }
  }, [active, user?.id])

  // RL-020 #5: pick up tenant setting changes without a full reload — refetch the
  // session config when the tab regains focus/visibility (e.g. after an admin saves
  // the Session Timeout settings). Staff already logged in otherwise keep stale config.
  useEffect(() => {
    if (!active) return
    const refetch = () => {
      fetchSessionConfig().then((cfg) => {
        if (cfg) setConfig(cfg)
      })
    }
    const onFocus = () => refetch()
    const onVisible = () => {
      if (document.visibilityState === 'visible') refetch()
    }
    window.addEventListener('focus', onFocus)
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      window.removeEventListener('focus', onFocus)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [active, user?.id])

  const closeWarning = useCallback(() => {
    warningDeadlineRef.current = null
    setShowWarning(false)
  }, [])

  const stayLoggedIn = useCallback(() => {
    resetActivity()
    closeWarning()
  }, [closeWarning])

  const terminate = useCallback(
    async (reason: 'idle' | 'manual') => {
      if (terminatingRef.current) return
      terminatingRef.current = true
      warningDeadlineRef.current = null
      setShowWarning(false)

      const currentRole = authStorage.getUser()?.role

      // Set a sessionStorage flag BEFORE the async gap so the login page can
      // show "Session expired due to inactivity" even if another redirect (e.g.
      // StaffNotification 401) fires during the await and overwrites our URL.
      // sessionStorage survives same-tab navigation; the login page consumes it. (RL-020 R8/AC6)
      if (reason === 'idle') {
        sessionStorage.setItem('__rl020_expired', '1')
      }

      await invalidateServerSession() // real logout (best-effort)
      clearAuthSession()

      const tenant = getCurrentTenant()
      const path = getLoginRedirectPath(currentRole)
      const params = new URLSearchParams({ tenant })
      if (reason === 'idle') params.set('session', 'expired') // RL-020 R8/AC6
      window.location.href = `${path}?${params.toString()}`
    },
    [],
  )

  // The heartbeat: evaluate inactivity on a fixed cadence. Timestamp-based so a
  // background-throttled tab still times out correctly (R12/AC10).
  useEffect(() => {
    if (!enabled || !config) return

    const listener = () => recordActivity()
    ACTIVITY_EVENTS.forEach((evt) =>
      window.addEventListener(evt, listener, { passive: true, capture: true }),
    )

    const evaluate = () => {
      if (terminatingRef.current) return

      // AC3: inside scheduled shift (operating hours) → never time out. Keep the
      // baseline fresh so leaving the shift starts a full inactivity window.
      // Shift-aware gate only applies to staff-portal roles; customers and platform
      // admins have no shift hours and should always be subject to inactivity timeout.
      if (isStaffRole && config && config.shiftAware && isWithinOperatingHours(config)) {
        resetActivity()
        if (warningDeadlineRef.current !== null) closeWarning()
        return
      }

      // R13/AC8: pause during uploads / report generation.
      if (isBusy()) {
        resetActivity()
        if (warningDeadlineRef.current !== null) closeWarning()
        return
      }

      const now = Date.now()

      if (warningDeadlineRef.current === null) {
        const idle = now - getLastActivity()
        if (idle >= timeoutMinutes * 60_000) {
          warningDeadlineRef.current = now + config.warningSeconds * 1000
          setUnsavedAtWarning(hasUnsavedChanges()) // R9/AC7 snapshot
          setRemainingMs(config.warningSeconds * 1000)
          setShowWarning(true)
        }
      } else {
        const remaining = warningDeadlineRef.current - now
        setRemainingMs(Math.max(0, remaining))
        if (remaining <= 0) {
          void terminate('idle') // R7/AC5
        }
      }
    }

    const intervalId = window.setInterval(evaluate, 1000)
    const onVisible = () => {
      if (document.visibilityState === 'visible') evaluate()
    }
    document.addEventListener('visibilitychange', onVisible)

    evaluate()

    return () => {
      window.clearInterval(intervalId)
      document.removeEventListener('visibilitychange', onVisible)
      ACTIVITY_EVENTS.forEach((evt) =>
        window.removeEventListener(evt, listener, { capture: true } as EventListenerOptions),
      )
    }
  }, [enabled, config, timeoutMinutes, closeWarning, terminate, isStaffRole])

  // RL-020 #1/#2: on every change to the active session, start from a clean slate.
  // Clearing the warning stops a stale countdown from reappearing after a logout →
  // re-login in the same tab (SPA navigation, no full reload). Resetting the activity
  // baseline when a staff session becomes active stops a timestamp left over from an
  // idle login page (module-load time) from firing the warning immediately on login.
  useEffect(() => {
    closeWarning()
    if (active) {
      terminatingRef.current = false
      resetActivity()
    }
  }, [active, user?.id, closeWarning])

  if (!enabled || !showWarning || !config) return null

  const totalMs = config.warningSeconds * 1000
  const pct = totalMs > 0 ? Math.max(0, Math.min(100, (remainingMs / totalMs) * 100)) : 0

  return (
    <Dialog open={showWarning} onOpenChange={() => {}}>
      <DialogContent
        className="[&>button]:hidden sm:max-w-md"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Are you still there?</DialogTitle>
          <DialogDescription>
            Your session is about to expire due to inactivity. You&apos;ll be logged out in{' '}
            <span className="font-semibold tabular-nums text-foreground">
              {formatCountdown(remainingMs)}
            </span>
            .
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <Progress value={pct} className="h-2" />
          {unsavedAtWarning && (
            <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm font-medium text-destructive">
              You have unsaved changes that will be lost.
            </p>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={() => terminate('manual')}>
            Logout Now
          </Button>
          <Button onClick={stayLoggedIn}>Stay Logged In</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
